import type { FC, PropsWithChildren } from 'react';
import { Component } from 'react';

import { useTrackLoader } from 'platform/components/loader/LoaderContext';
import { RouteContentCommitSignal } from 'platform/components/loader/RouteContentCommitSignal';
import { UnauthenticatedError, UnauthorizedError } from 'platform/Error';
import { initialLoginLiteUrl } from 'platform/loginLiteCache';
import { getLoginUrl } from 'platform/loginRedirect';
// eslint-disable-next-line no-restricted-imports -- top level error boundary to handle error
import { WrongAccountPage } from 'sellDirect/routes/errors/WrongAccountPage';
// eslint-disable-next-line no-restricted-imports -- top level error boundary to handle error
import { NotAuthorizedPage } from 'shared/routes/errors/NotAuthorizedPage';

import navigationHelpers from './navigationHelpers';

type CustomerBoundaryProps = PropsWithChildren & {
  authenticationError?: UnauthenticatedError;
  authorizationError?: UnauthorizedError;
};

/**
 * Implements componentDidCatch to track the error in observability to handle unauthenticated errors.
 *
 * Rethrows the error if it is not an UnauthenticatedError.
 */
export class CustomerBoundary extends Component<
  CustomerBoundaryProps,
  {
    authenticationError?: UnauthenticatedError;
    authorizationError?: UnauthorizedError;
    hasError: boolean;
  }
> {
  /** Calls super */
  constructor(props: CustomerBoundaryProps) {
    super(props);

    if (props.authenticationError) {
      this.state = {
        authenticationError: props.authenticationError,
        authorizationError: undefined,
        hasError: true,
      };
    } else if (props.authorizationError) {
      this.state = {
        authorizationError: props.authorizationError,
        authenticationError: undefined,
        hasError: true,
      };
    }
  }

  static getDerivedStateFromError(error: UnauthenticatedError | UnauthorizedError | Error) {
    if (error instanceof UnauthenticatedError || error instanceof UnauthorizedError) {
      // Update state so the next render will show the fallback UI.
      return {
        hasError: true,
        authenticationError: error instanceof UnauthenticatedError ? error : undefined,
        authorizationError: error instanceof UnauthorizedError ? error : undefined,
      };
    }
  }

  /** @throws OD Team Boundary Error */
  override componentDidCatch(error: UnauthenticatedError | UnauthorizedError | Error) {
    if (error instanceof UnauthenticatedError) {
      this.setState({ authenticationError: error, hasError: true });
    } else if (error instanceof UnauthorizedError) {
      this.setState({ authorizationError: error, hasError: true });
    } else {
      // Just forward error has already been caught once by boundary
      throw error;
    }
  }

  override render() {
    return <CustomerBoundaryUI {...this.state}>{this.props.children}</CustomerBoundaryUI>;
  }
}

export class CustomerBoundaryUI extends Component<CustomerBoundaryProps> {
  override render() {
    if (this.props.authorizationError) {
      const error = this.props.authorizationError;
      if (error.accessToken) {
        this.redirectToLogin(this.props.authorizationError);
        return <RedirectLoader name="customer-boundary-redirect" />;
      }

      // If the error is related to accessing a sell direct offer, we show a different error page
      if (
        error.triggeredQuery === 'LatestSellerLead' &&
        error.accessErrorType === 'permission-denied'
      ) {
        return (
          <>
            <RouteContentCommitSignal />
            <WrongAccountPage />
          </>
        );
      }

      if (error.accessErrorType === 'reauthentication-required') {
        this.logout();
        // Renders loading page while the user is redirected to the logout page
        return <RedirectLoader name="customer-boundary-redirect" />;
      }

      return (
        <>
          <RouteContentCommitSignal />
          <NotAuthorizedPage />
        </>
      );
    }

    if (this.props.authenticationError) {
      this.redirectToLogin(this.props.authenticationError);
      return <RedirectLoader name="customer-boundary-redirect" />;
    }

    return this.props.children;
  }

  /**
   * Redirects the user to the login page with the appropriate query params.
   */
  redirectToLogin = (error: UnauthenticatedError | UnauthorizedError) => {
    let label = 'login';
    if (error.accessToken) {
      // If there is no user but there is an `access_token` param in the URL, we will
      // redirect to the login lite url (opendoor.com/token)
      label = 'login-login-lite';
    } else if ('emailPrefill' in error && !!error.emailPrefill) {
      // If the user is not authenticated we redirect to the login page.
      // If there is an `emailPrefill` param in the URL, we add it as a query param
      // to pre-populate the log-in/sign-up form
      label = 'login-email-prefill';
    }

    // When access_token is present, use the initial URL (captured before any replaceState
    // stripping) so the login-lite redirect_url correctly includes the token — allowing the
    // user to return to the same URL after login, at which point the token gets stripped.
    const loginHref = error.accessToken ? initialLoginLiteUrl : window.location.href;

    // Redirect customers to the login page if they are not authenticated
    navigationHelpers.hardNavReplace(getLoginUrl(loginHref), {
      label,
      // oxlint-disable-next-line typescript/no-misused-spread -- `stack` is non-enumerable on Error, so the spread alone drops it; it is re-added explicitly.
      data: { ...error, message: error.message, stack: error.stack },
    });
  };

  /**
   * Redirects the user to the login page with the appropriate query params.
   */
  logout = () => navigationHelpers.hardNavReplace(getLoginUrl(window.location.href));
}

/**
 * Registers a loading source from inside `CustomerBoundaryUI`'s class render path,
 * so the global loader covers the screen while the user is being navigated to
 * login / logout.
 */
const RedirectLoader: FC<{ name: string }> = ({ name }) => {
  useTrackLoader(name, true);
  return null;
};
